/* eslint-disable no-console */
import '@cbhq/cds-fonts/fonts.css';
import '@cbhq/cds-icons/fonts/web/icon-font.css';
import '@cbhq/cds-icons/fonts/web/icon-font.css';
import '@cbhq/cds-web/defaultFontStyles';
import '@cbhq/cds-web/globalStyles';
import './index.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createGraphqlEnvironment, setDefaultLogCallback } from '@cbhq/data-layer';
import { convertToCBSupported, fetchMessagesFromCDN, getClientLocale } from '@cbhq/intl';
import { polyfillIntl } from '@cbhq/intl/polyfills';

import { initAnalytics } from './utils/analytics/initAnalytics';
import { initErrorVitals } from './utils/errorVitals';
import { apiFetch } from './utils/fetch';
import { initInstantApiHooks } from './utils/initInstantApiHooks';
import { initKillSwitches } from './utils/killSwitches/initKillSwitches';
import { logError } from './utils/logging';
import { App } from './App';
import persistedQueries from './persisted_queries.json';

async function bootstrap() {
  initErrorVitals();
  initInstantApiHooks();
  initAnalytics();

  setDefaultLogCallback((level, ...messages) => {
    if (level === 'ERROR') {
      console.error(messages);
      const error =
        messages.find((message): message is Error => message instanceof Error) ?? messages[0];
      logError(error, { context: 'workspace.data-layer' });
      return;
    }

    console.log(messages);
  });

  const environment = createGraphqlEnvironment({
    graphqlUrl: '/graphql/query',
    getPersistedQueries: async () => persistedQueries,
    networkFetch: apiFetch,
  });

  const locale = convertToCBSupported(getClientLocale());
  let { messages } = { messages: {} };

  try {
    const [, messagesPayload] = await Promise.all([
      polyfillIntl(locale),
      process.env.SMARTLING_PROJECT_ID
        ? fetchMessagesFromCDN(locale, process.env.SMARTLING_PROJECT_ID, undefined, {
            env: process.env.RELEASE_STAGE,
          })
        : Promise.resolve({}),
      initKillSwitches(),
    ]);

    messages = messagesPayload;

    if (process.env.NODE_ENV !== 'production') {
      console.group('%cApp Info', 'color: rgb(115, 162, 255)');
      console.log({ APP_VERSION: process.env.APP_VERSION });
      console.groupEnd();
    }

    createRoot(document.getElementById('root') as HTMLElement).render(
      <StrictMode>
        <App locale={locale} messages={messages} environment={environment} />
      </StrictMode>,
    );
  } catch (error) {
    console.error('Workspace bootstrap failed', error);
    logError(error);
  }
}

void bootstrap();
